import { create } from "zustand";

import type { ReplaySpeed } from "@/stores/replayStore";

export interface ReplayResumeSnapshot {
  symbol: string;
  symbolDisplayName: string;
  entryTimeframe: string;
  startDate: number;
  exitTimestamp: number;
  speed: ReplaySpeed;
  contextCandleCount: number;
  candleCount: number;
  lineColor: string;
  capturedAt: number;
}

interface ReplayResumeState {
  snapshot: ReplayResumeSnapshot | null;
  entryTimeframe: string | null;
  isVisible: boolean;
  pendingSeekTimestamp: number | null;
  dismissTimerId: ReturnType<typeof setTimeout> | null;

  setEntryTimeframe: (tf: string) => void;
  capture: (snapshot: ReplayResumeSnapshot) => void;
  show: () => void;
  dismiss: () => void;
  clear: () => void;
  setPendingSeek: (timestamp: number | null) => void;
}

const TOAST_DURATION_MS = 12_000;

export const useReplayResumeStore = create<ReplayResumeState>()((set, get) => ({
  snapshot: null,
  entryTimeframe: null,
  isVisible: false,
  pendingSeekTimestamp: null,
  dismissTimerId: null,

  setEntryTimeframe: (tf) => set({ entryTimeframe: tf }),

  capture: (snapshot) => {
    const prev = get().dismissTimerId;
    if (prev) clearTimeout(prev);
    set({ snapshot, dismissTimerId: null });
  },

  show: () => {
    const { snapshot, dismissTimerId } = get();
    if (!snapshot) return;
    if (dismissTimerId) clearTimeout(dismissTimerId);

    const id = setTimeout(() => {
      get().clear();
    }, TOAST_DURATION_MS);

    set({ isVisible: true, dismissTimerId: id });
  },

  dismiss: () => {
    const { dismissTimerId } = get();
    if (dismissTimerId) clearTimeout(dismissTimerId);
    set({ isVisible: false, dismissTimerId: null });
    // Small delay for exit animation before clearing snapshot
    setTimeout(() => {
      const current = get();
      if (!current.isVisible) {
        set({ snapshot: null });
      }
    }, 300);
  },

  clear: () => {
    const { dismissTimerId } = get();
    if (dismissTimerId) clearTimeout(dismissTimerId);
    set({ snapshot: null, isVisible: false, dismissTimerId: null, pendingSeekTimestamp: null });
  },

  setPendingSeek: (timestamp) => set({ pendingSeekTimestamp: timestamp }),
}));
